import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { Grid, Typography, units } from '@ha/dsl';
import { CalendarLock1Light } from '@ha/icons';

import { TranslateFunc } from '../types';

const useStyles = makeStyles(theme => ({
  calendarInfoContainer: {
    display: 'block',
    padding: units.trbl(1, 1.25, 1, 1.25),

    [theme.breakpoints.up('sm')]: {
      padding: units.yx(0, 1.5),
    },
  },
  calendarInfoItem: {
    marginBottom: units.rem(0.5),
  },
  moveInWindowHintIcon: {
    position: 'relative',
    top: '-1px',
    height: units.rem(0.75),
    width: units.rem(0.75),
    marginRight: units.rem(0.5),
    border: units.border(1, 'solid', theme.palette.secondary.dark),
    borderRadius: '50%',
  },
  minimumPeriodHintIcon: {
    height: units.rem(1),
    width: units.rem(1),
    marginRight: units.rem(0.25),
  },
  calendarAdditionalInfo: {
    flexDirection: 'column',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  moveInWindowHintContainer: {
    paddingBottom: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
}));

interface Props {
  additionalInfo?: React.ReactNode;
  minimumPeriod?: number;
  hasMoveInWindowHint?: boolean;
  T: TranslateFunc;
}

const { Caption } = Typography;

const CalendarInfo: React.FC<Props> = ({
  additionalInfo,
  minimumPeriod,
  hasMoveInWindowHint = false,
  T,
}) => {
  const classes = useStyles();

  const minimumPeriodHint = React.useMemo(
    () =>
      minimumPeriod
        ? T(
            'date_picker.rental_period.min',
            `${minimumPeriod}-${
              minimumPeriod === 1 ? T('month') : T('months')
            }`,
          )
        : '',
    [T, minimumPeriod],
  );

  if (!additionalInfo && !minimumPeriodHint && !hasMoveInWindowHint) {
    return null;
  }

  return (
    <Grid
      container
      direction="column"
      className={classes.calendarInfoContainer}
    >
      {additionalInfo && (
        <Grid
          item
          className={classes.calendarInfoItem}
          data-test-locator="CalendarInfo / Additional info"
        >
          {additionalInfo}
        </Grid>
      )}
      <Grid item>
        <Grid container className={classes.calendarAdditionalInfo}>
          {hasMoveInWindowHint && (
            <Grid item className={classes.calendarInfoItem}>
              <Grid
                container
                alignItems="center"
                className={classes.moveInWindowHintContainer}
              >
                <Grid item className={classes.moveInWindowHintIcon} />
                <Grid item>
                  <Caption data-test-locator="CalendarInfo / Move-in hint">
                    {T('date_picker.move_in_window.legend_label')}
                  </Caption>
                </Grid>
              </Grid>
            </Grid>
          )}
          {minimumPeriodHint && (
            <Grid item className={classes.calendarInfoItem}>
              <Grid container alignItems="center">
                <CalendarLock1Light className={classes.minimumPeriodHintIcon} />
                <Grid item>
                  <Caption data-test-locator="CalendarInfo / Minimal period hint">
                    {minimumPeriodHint}
                  </Caption>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(CalendarInfo);
